import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
    getFakeContacts,
    getFakeContactById,
    getFakeCompanies,
    getFakeCompanyById,
    getFakeServices,
    getFakeServiceById,
    getFakeVenues,
    getFakeVenueById,
    getContactsForCompany,
    getStaffForVenue,
    clearFakeDataCache
} from '@/services/fakeDataService';

export const useFakeDataStore = defineStore('fakeData', () => {
    // State
    const contacts = ref([]);
    const companies = ref([]);
    const services = ref([]);
    const venues = ref([]);
    const isLoaded = ref(false);
    const selectedContactId = ref(null);
    const selectedCompanyId = ref(null);
    const selectedServiceId = ref(null);
    const selectedVenueId = ref(null);

    // Getters
    const allContacts = computed(() => contacts.value);
    const allCompanies = computed(() => companies.value);
    const allServices = computed(() => services.value);
    const allVenues = computed(() => venues.value);

    const selectedContact = computed(() => {
        if (!selectedContactId.value) return null;
        return contacts.value.find(c => c.id.toString() === selectedContactId.value.toString()) ||
            getFakeContactById(selectedContactId.value);
    });

    const selectedCompany = computed(() => {
        if (!selectedCompanyId.value) return null;
        return companies.value.find(c => c.id.toString() === selectedCompanyId.value.toString()) ||
            getFakeCompanyById(selectedCompanyId.value);
    });

    const selectedService = computed(() => {
        if (!selectedServiceId.value) return null;
        return services.value.find(s => s.id.toString() === selectedServiceId.value.toString()) ||
            getFakeServiceById(selectedServiceId.value);
    });

    const selectedVenue = computed(() => {
        if (!selectedVenueId.value) return null;
        return venues.value.find(v => v.id.toString() === selectedVenueId.value.toString()) ||
            getFakeVenueById(selectedVenueId.value);
    });

    const companyContacts = computed(() => {
        if (!selectedCompanyId.value) return [];
        return getContactsForCompany(selectedCompanyId.value);
    });

    const venueStaff = computed(() => {
        if (!selectedVenueId.value) return [];
        return getStaffForVenue(selectedVenueId.value);
    });

    // Actions
    function loadAllData() {
        contacts.value = getFakeContacts();
        companies.value = getFakeCompanies();
        services.value = getFakeServices();
        venues.value = getFakeVenues();
        isLoaded.value = true;
        return {
            contacts: contacts.value,
            companies: companies.value,
            services: services.value,
            venues: venues.value
        };
    }

    function refreshData() {
        clearFakeDataCache();
        return loadAllData();
    }

    function setSelectedContact(id) {
        selectedContactId.value = id;
        return selectedContact.value;
    }

    function setSelectedCompany(id) {
        selectedCompanyId.value = id;
        return selectedCompany.value;
    }

    function setSelectedService(id) {
        selectedServiceId.value = id;
        return selectedService.value;
    }

    function setSelectedVenue(id) {
        selectedVenueId.value = id;
        return selectedVenue.value;
    }

    function getFilteredContacts(searchText = '', page = 1, pageSize = 10, sortBy = 'name') {
        if (!contacts.value.length) {
            loadAllData();
        }

        let filtered = [...contacts.value];

        // Apply search filter
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(contact =>
                contact.firstName.toLowerCase().includes(search) ||
                contact.lastName.toLowerCase().includes(search) ||
                contact.email.toLowerCase().includes(search) ||
                (contact.company && contact.company.toLowerCase().includes(search))
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            if (sortBy === 'name') {
                return `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`);
            }
            if (sortBy === 'company') {
                return (a.company || '').localeCompare(b.company || '');
            }
            if (sortBy === 'email') {
                return a.email.localeCompare(b.email);
            }
            return 0;
        });

        // Calculate pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            pagination: {
                page,
                pageSize,
                totalItems,
                totalPages
            }
        };
    }

    function getFilteredCompanies(searchText = '', page = 1, pageSize = 10, sortBy = 'name') {
        if (!companies.value.length) {
            loadAllData();
        }

        let filtered = [...companies.value];

        // Apply search filter
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(company =>
                company.name.toLowerCase().includes(search) ||
                company.type.toLowerCase().includes(search) ||
                company.email.toLowerCase().includes(search)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            if (sortBy === 'name') {
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'type') {
                return a.type.localeCompare(b.type);
            }
            if (sortBy === 'email') {
                return a.email.localeCompare(b.email);
            }
            return 0;
        });

        // Calculate pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            pagination: {
                page,
                pageSize,
                totalItems,
                totalPages
            }
        };
    }

    function getFilteredServices(searchText = '', page = 1, pageSize = 10, sortBy = 'name') {
        if (!services.value.length) {
            loadAllData();
        }

        let filtered = [...services.value];

        // Apply search filter
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(service =>
                service.name.toLowerCase().includes(search) ||
                service.type.toLowerCase().includes(search) ||
                service.description.toLowerCase().includes(search)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            if (sortBy === 'name') {
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'type') {
                return a.type.localeCompare(b.type);
            }
            if (sortBy === 'price') {
                // Extract and compare numeric values from price strings
                const priceA = parseFloat(a.pricePerHour.replace(/[^0-9.]/g, '')) || 0;
                const priceB = parseFloat(b.pricePerHour.replace(/[^0-9.]/g, '')) || 0;
                return priceA - priceB;
            }
            return 0;
        });

        // Calculate pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            pagination: {
                page,
                pageSize,
                totalItems,
                totalPages
            }
        };
    }

    function getFilteredVenues(searchText = '', page = 1, pageSize = 10, sortBy = 'name') {
        if (!venues.value.length) {
            loadAllData();
        }

        let filtered = [...venues.value];

        // Apply search filter
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(venue =>
                venue.name.toLowerCase().includes(search) ||
                venue.type.toLowerCase().includes(search) ||
                venue.description.toLowerCase().includes(search) ||
                venue.email.toLowerCase().includes(search)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            if (sortBy === 'name') {
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'type') {
                return a.type.localeCompare(b.type);
            }
            if (sortBy === 'capacity') {
                return a.capacity - b.capacity;
            }
            return 0;
        });

        // Calculate pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            pagination: {
                page,
                pageSize,
                totalItems,
                totalPages
            }
        };
    }

    // Initialize store if needed
    if (!isLoaded.value) {
        loadAllData();
    }

    return {
        // State
        contacts,
        companies,
        services,
        venues,
        isLoaded,
        selectedContactId,
        selectedCompanyId,
        selectedServiceId,
        selectedVenueId,

        // Getters
        allContacts,
        allCompanies,
        allServices,
        allVenues,
        selectedContact,
        selectedCompany,
        selectedService,
        selectedVenue,
        companyContacts,
        venueStaff,

        // Actions
        loadAllData,
        refreshData,
        setSelectedContact,
        setSelectedCompany,
        setSelectedService,
        setSelectedVenue,
        getFilteredContacts,
        getFilteredCompanies,
        getFilteredServices,
        getFilteredVenues
    };
}); 